/**
 * WHO GETS IN, in one place, as three pure rules.
 *
 * These used to be three expressions scattered through App.tsx, which is how the gates came
 * to disagree with each other: doors that asked, side entrances that did not, and a review
 * build where none of them asked at all. Pure functions here so the rules can be tested
 * without a browser, an account, or a shipped app build.
 *
 * House style: no em dashes or en dashes.
 */

/**
 * May this reader use the member features (Deep dive, the daily Brief, Search, Ask)?
 *
 * EITHER signal is proof, and both are compared to `true` rather than read as truthy,
 * because both are null while auth is still resolving and a member surface must be shut
 * during that window rather than open and then be snatched back.
 *
 *  - `firebaseAuthed`: Firebase reports a signed-in session for this device. That IS the
 *    requirement (a valid Google, Apple or email-code sign-in), and it is known as soon as
 *    the persisted session restores, with no network at all.
 *  - `accountSignedIn`: the worker's profile row, which needs a round trip to know.
 *
 * The worker signal alone was not enough, and the APP is where that would have hurt: one
 * failed /account call (a tunnel, a flaky connection, a cold worker) and a reader who is
 * demonstrably signed in gets told to sign in. Reading works offline in the app; being
 * recognised has to as well.
 */
export function isMember(
  firebaseAuthed: boolean | null | undefined,
  accountSignedIn: boolean | null | undefined,
): boolean {
  return firebaseAuthed === true || accountSignedIn === true;
}

/**
 * May the first door be skipped?
 *
 * WEB YES, APP NO, and the asymmetry is a decision rather than an oversight. On the web,
 * 78% of readers read exactly one card and 96% never return, so demanding an account before
 * the first word is the bottleneck. Someone who has already installed the app has answered
 * that question, and the operator's standing decision is that app sign-in is mandatory.
 * A shipped app build carrying a Skip button would reverse that silently.
 */
export function canSkipFirstDoor(native: boolean): boolean {
  return !native;
}

/**
 * How many stories must be BEHIND a web reader before the invitation stands.
 *
 * Counted in finished stories, not sessions and not page views, because a refresh must
 * neither spend the allowance nor reset it. The count comes off the persisted seen set.
 */
export const INVITE_AFTER_CARDS = 3;

/**
 * Does the sign-in card stand in front of this reader right now?
 *
 * `firebaseAuthed` is deliberately compared to `false`: while it is still null nobody knows
 * yet, and throwing up a sign-in card at a reader who turns out to be signed in is worse
 * than a moment of nothing.
 *
 * THE WEB DOOR NOW STANDS AFTER THE VALUE, NOT IN FRONT OF IT (2026-08-27, USER decision).
 * The landing page promises "no account needed to start reading" and the next screen used to
 * ask for an account, which is both a broken promise and a question asked before anything has
 * been given. The invitation waits until `INVITE_AFTER_CARDS` stories are finished, so it
 * arrives when the reader has something to keep: a real read count, a streak that starts now,
 * and saves worth syncing. Asking then converts on evidence rather than on hope.
 *
 * THE APP DOOR IS UNCHANGED: mandatory and immediate, per the standing 2026-08-09 decision.
 * `storiesFinished` is not consulted on native at all, so no future tuning of the web
 * threshold can quietly open the app without an account.
 */
export function showsFirstDoor(
  firebaseAuthed: boolean | null | undefined,
  wallSkipped: boolean,
  native: boolean,
  storiesFinished: number,
): boolean {
  if (firebaseAuthed !== false) return false;
  // The app: no skip, no waiting, sign-in is the way in.
  if (!canSkipFirstDoor(native)) return true;
  if (wallSkipped) return false;
  return storiesFinished >= INVITE_AFTER_CARDS;
}
